// @givmo/fundraisers-module — the Layers tab's dialogs (FCM-WEB25; frames 03 to 05b): add a layer below
// (decision 2: one dialog, three ways), add a layer above, move a layer (the parent's side, Q5), and which
// level's card the pages under a campaign show (decision 3). FCM-WEB33 (D-FCM-27, D-FCM-35): "Add a layer
// above" gains a second way, a campaign someone else runs, whose ONE join setting decides whether this
// campaign joins it at once, asks first, or needs its invitation (frames 05 to 05e).
//
// No new create path exists (plan §FC-16.2): "A new campaign" composes the module's own create door, the
// existing upgrade and the attach, three calls and not one transaction. A failure midway leaves a campaign
// that is not yet attached, which the organizer can attach or delete. The new campaign starts with exactly
// the charities the campaign it joins requires, so the attach's recipient check passes: the charity
// flavor's own row raises for the charity itself; a `chosen` row takes the joined campaign's recipients,
// unless that campaign lets its pages and layers pick: then the new one picks its own in the dialog, from
// the list that binds it if one does, because a linked campaign's charities can't change once it is
// attached (FCM-BE31's lock holds at both ends), so a pick left for later could never be made.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  sdk,
  type OrgFundraiserCreate,
  type P2pConfigOut,
  type P2pConfigRequest,
  type P2pLayerCandidateOut,
  type P2pLayerJoinPreviewOut,
  type P2pLayerNodeOut,
  type P2pLayerStructureOut,
} from '@givmo/api-client';
import {
  Avatar,
  Button,
  Dialog,
  RadioTile,
  RadioTileGroup,
  TextField,
  cn,
  toast,
} from '@givmo/design-system';
import { CircleAlert, Info } from 'lucide-react';
import { useFundraisersModule } from './fundraisers-context';
import { privateFundraiserOptions } from './fundraisers-queries';
import { campaignOptions, TEAMS_OFF } from './campaign-queries';
import { Chips, idsOf, recipientKey } from './campaign-settings-cards';
import { fundraiserStateLabel } from './fundraisers-decode';
import { FundraisersLoading, ModuleLink } from './fundraisers-state';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import { StateNotice } from './lifecycle-ui';
import { displayP2pDate, displayP2pMoney } from './p2p-decode';
import { REQUESTS_COPY as R, REQUEST_SERVER_WORDS } from './join-requests-copy';
import { invalidateRequestReads, joinParentPreviewOptions } from './join-requests-queries';
import { PickerResults, RecipientSearch } from './recipient-picker';
import { useRecipientNames } from './recipient-names';
import { currentRecipients, type RecipientItem } from './recipients-decode';
import { LAYERS_COPY as C, SERVER_WORDS, series } from './layers-copy';
import { campaignIdFromLink, layerRefusal, recipientChoiceLive } from './layers-decode';
import { SEASON_COPY as SC } from './season-setup-copy';
import {
  CANDIDATE_PAGE,
  invalidateLayerReads,
  layerCandidatesOptions,
  publicCampaignOptions,
  publicFundraiserOptions,
} from './layers-queries';
import type { LayerTarget } from './layers-tab';
import { useLivePage } from './use-live-page';

export function DialogFrame({
  open,
  onOpenChange,
  title,
  description,
  testId,
  children,
  footer,
  width,
  sticky = false,
  footerNote = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  testId: string;
  children: ReactNode;
  footer: ReactNode;
  /** Wider than the design system's 412, for long terms (FCM-WEB33). */
  width?: number;
  /**
   * FCM-WEB33: the consent terms are long, so the title and the footer stay put and the middle
   * scrolls (the design system's sticky-chrome idiom); `footerNote` sits right above the buttons.
   */
  sticky?: boolean;
  footerNote?: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        aria-label={title}
        className={cn(
          'fundraisers-dialog flex flex-col',
          sticky ? 'gap-0 overflow-hidden p-0' : 'gap-16',
        )}
        testId={testId}
        width={width}
      >
        {sticky ? (
          <>
            <div className="px-24 pb-12 pt-24">
              <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
                {title}
              </Dialog.Title>
            </div>
            <div
              className="flex min-h-0 flex-1 flex-col gap-16 overflow-y-auto px-24 pb-16"
              data-testid={`${testId}-scroll`}
            >
              {children}
            </div>
            <div className="flex flex-col gap-12 border-t border-border-subtle px-24 pb-24 pt-16">
              {footerNote}
              <div className="flex items-center justify-end gap-8">{footer}</div>
            </div>
          </>
        ) : (
          <>
            <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              {title}
            </Dialog.Title>
            {description && (
              <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {description}
              </Dialog.Description>
            )}
            {children}
            <div className="mt-8 flex items-center justify-end gap-8">{footer}</div>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * Every page of "A campaign you run" (FCM-BE63 pages it by 25), the next one read as the list's end comes
 * into view in its scrolling box.
 */
function useCandidatePages(campaignId: string, open: boolean, move?: string) {
  const [pages, setPages] = useState(1);
  const results = useQueries({
    queries: Array.from({ length: pages }, (_, page) => ({
      ...layerCandidatesOptions(campaignId, page * CANDIDATE_PAGE, move),
      enabled: open && campaignId !== '',
      retry: false,
    })),
  });
  const rows = results.flatMap((result) => result.data ?? []);
  const last = results[results.length - 1];
  const more = last?.data?.length === CANDIDATE_PAGE;
  const end = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = end.current;
    if (!node || !more || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setPages((now) => now + 1);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [more, pages]);
  return {
    rows,
    pending: results[0]?.isPending ?? true,
    end,
    reset: () => setPages(1),
  };
}

/** What a new campaign under or over `target` starts with: the charities that campaign requires. */
async function charitiesFor(
  client: ReturnType<typeof useQueryClient>,
  target: string,
  mode: 'self' | 'chosen',
): Promise<Pick<OrgFundraiserCreate, 'charity_ids' | 'etf_ids' | 'collection_ids'>> {
  if (mode === 'self') return {};
  const overview = await client.fetchQuery(campaignOptions(target));
  if (overview.config.recipient_choice === 'supporters') return {};
  const own = currentRecipients(await client.fetchQuery(privateFundraiserOptions(target)));
  return {
    charity_ids: own.filter(({ kind }) => kind === 'charity').map(({ id }) => id),
    etf_ids: own.filter(({ kind }) => kind === 'etf').map(({ id }) => id),
    collection_ids: own.filter(({ kind }) => kind === 'collection').map(({ id }) => id),
  };
}

export function AddBelowDialog({
  target,
  self,
  config,
  selfCharities,
  seasonTemplate = false,
  onClose,
  onDone,
}: {
  /** The campaign the new layer goes under; `null` while the dialog is closed. */
  target: LayerTarget | null;
  self: LayerTarget;
  /** This campaign's settings, which say who picks its charities. */
  config: P2pConfigOut | null;
  /** The names of this campaign's own charities, for the charities line. */
  selfCharities: readonly string[];
  /** FCM-WEB34: this campaign is a season set up, carrying a pledge template that names whom it counts. */
  seasonTemplate?: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const module = useFundraisersModule();
  const client = useQueryClient();
  const live = useLivePage();
  const open = target !== null;
  const row = target ?? self;
  const isSelf = row.id === self.id;
  const [what, setWhat] = useState('new');
  const [name, setName] = useState('');
  const [picked, setPicked] = useState('');
  const [link, setLink] = useState('');
  // The server's refusal of the pasted campaign, kept until the link changes (frames 03e and 03f).
  const [inviteRefusal, setInviteRefusal] = useState<{ id: string; message: string } | null>(null);
  // The campaign the new layer goes under: whether its pages and layers pick their charities, and the
  // list that binds those picks (its own, or the nearest one above it).
  const under = useQuery({ ...campaignOptions(row.id), enabled: open, retry: false });
  const picksOwn =
    module.flavor.recipientMode === 'chosen' &&
    under.data?.config.recipient_choice === 'supporters';
  const binding = under.data?.config.binding_list ?? null;
  const listNames = useRecipientNames(binding, open && picksOwn);
  const [picks, setPicks] = useState<RecipientItem[]>([]);
  const pickedKeys = new Set(picks.map(recipientKey));
  const pick = (item: RecipientItem) =>
    setPicks((now) =>
      now.some((p) => recipientKey(p) === recipientKey(item)) ? now : [...now, item],
    );
  const candidates = useCandidatePages(row.id, open && what === 'yours');
  const linkedId = what === 'others' ? campaignIdFromLink(link) : null;
  const preview = useQuery({
    ...publicCampaignOptions(linkedId ?? ''),
    enabled: open && linkedId !== null,
    retry: false,
  });
  const byline = useQuery({
    ...publicFundraiserOptions(linkedId ?? ''),
    enabled: open && linkedId !== null,
    retry: false,
  });
  const refusedHere = inviteRefusal !== null && inviteRefusal.id === linkedId;
  useEffect(() => {
    // Each open starts fresh.
    if (!open) {
      setWhat('new');
      setName('');
      setPicked('');
      setLink('');
      setInviteRefusal(null);
      setPicks([]);
    }
  }, [open]);
  const add = useMutation({
    mutationFn: async (): Promise<'added' | 'invited'> => {
      if (what === 'new') {
        if (!module.orgRef) throw new Error('This organization is not ready.');
        if (picksOwn && picks.length === 0) throw new Error('Choose its recipients first.');
        const charities = picksOwn
          ? idsOf(picks)
          : await charitiesFor(client, row.id, module.flavor.recipientMode);
        const { data: created } = await sdk.orgFundraisersCreateOrgFundraiser({
          path: { org_ref: module.orgRef },
          body: { name: name.trim(), show_statistics: true, ...charities },
          throwOnError: true,
        });
        await sdk.campaignsEnableCampaign({
          path: { fundraiser_id: created.id },
          body: TEAMS_OFF,
          throwOnError: true,
        });
        await sdk.campaignsAttachCampaignLayer({
          path: { fundraiser_id: row.id },
          body: { child_campaign_id: created.id },
          throwOnError: true,
        });
        return 'added';
      }
      if (what === 'yours') {
        await sdk.campaignsAttachCampaignLayer({
          path: { fundraiser_id: row.id },
          body: { child_campaign_id: picked },
          throwOnError: true,
        });
        return 'added';
      }
      await sdk.campaignsSendCampaignLayerInvite({
        path: { fundraiser_id: row.id },
        body: { child_campaign_id: linkedId ?? '' },
        throwOnError: true,
      });
      return 'invited';
    },
    onSuccess: async (outcome) => {
      await onDone();
      if (live()) {
        onClose();
        toast.success(outcome === 'invited' ? 'Invitation created' : C.layerAdded);
      }
    },
    onError: async (error) => {
      if (!live()) return;
      const refusal = layerRefusal(error);
      if (what === 'others' && linkedId !== null && !refusal.stale) {
        // Said where the frames say it, under the pasted campaign, with no invitation created.
        setInviteRefusal({ id: linkedId, message: refusal.message });
        return;
      }
      toast.error(refusal.message);
      // The campaign it refused may have changed under the dialog: read the choices again (03d).
      setPicked('');
      candidates.reset();
      await invalidateLayerReads(client);
      if (refusal.stale) {
        onClose();
        void onDone();
      }
    },
  });
  const ready =
    what === 'new'
      ? name.trim().length > 0 &&
        // A `chosen` row starts with what the campaign above requires, read before it is created.
        (module.flavor.recipientMode === 'self' || under.isSuccess) &&
        (!picksOwn || picks.length > 0)
      : what === 'yours'
        ? picked !== ''
        : linkedId !== null && !refusedHere;
  const choice = config?.recipient_choice;
  const note =
    module.flavor.recipientMode === 'self'
      ? C.raisesForLike(module.orgName, row.name)
      : !isSelf
        ? null
        : choice === 'supporters'
          ? config?.binding_list
            ? // The list's own campaign: this one's, or the campaign above whose list binds it.
              C.picksOwnFromList(config.binding_list.campaign_name ?? C.anotherCampaign)
            : null
          : selfCharities.length > 0
            ? C.raisesForLike(series(selfCharities), row.name)
            : null;
  const listOffer = listNames.items
    .filter((item) => !pickedKeys.has(recipientKey(item)))
    .map((item) => ({ ...item, subtitle: null }));
  const eligible = candidates.rows.filter(
    (candidate) => candidate.can_manage && candidate.below?.can_add === true,
  );
  const taken = candidates.rows.filter(
    (candidate) => candidate.part_of != null && candidate.below?.can_add !== true,
  );
  return (
    <DialogFrame
      open={open}
      onOpenChange={(next) => {
        if (!next && !add.isPending) onClose();
      }}
      title={isSelf ? C.addBelow : C.addBelowTitleOn(row.name)}
      testId="layers-add-below-dialog"
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={add.isPending}
            onClick={onClose}
            testId="layers-add-below-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            loading={add.isPending}
            disabled={!ready || add.isPending}
            onClick={() => add.mutate()}
            testId="layers-add-below-confirm"
          >
            {what === 'others' ? C.createInvitation : C.addLayerConfirm}
          </Button>
        </>
      }
    >
      <RadioTileGroup
        value={what}
        onValueChange={setWhat}
        aria-label={C.whatToAdd}
        testId="layers-add-below-what"
      >
        <RadioTile
          value="new"
          label={C.optNew}
          description={C.optNewDesc}
          testId="layers-add-below-new"
        />
        <RadioTile
          value="yours"
          label={C.optYours}
          description={C.optYoursDesc}
          testId="layers-add-below-yours"
        />
        <RadioTile
          value="others"
          label={C.optOthers}
          description={C.optOthersDesc}
          testId="layers-add-below-others"
        />
      </RadioTileGroup>
      {/* D-FCM-61's decision 7 (review-71's Q2): an organizer's own new or attached layer takes no copy
          of the season's pledge template (only a join copies), and the dialog says so before anything
          is added. An invitation's accept copies as a join does, so its way carries no note. */}
      {isSelf && seasonTemplate && what !== 'others' && (
        <div
          className="flex items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-12"
          data-testid="layers-add-below-no-template"
        >
          <Info size={16} aria-hidden className="mt-1 shrink-0 text-text-secondary" />
          <p className="text-s13 font-w500 leading-h15 text-text-strong">
            {SC.addLayerNoTemplate(row.name)}
            <ModuleLink
              href={FUNDRAISERS_ROUTES.season(row.id)}
              testId="layers-add-below-add-games"
              className="whitespace-nowrap font-w600 text-brand-700 underline underline-offset-2 hover:text-brand-600"
            >
              {SC.addGames}
            </ModuleLink>
            {SC.addLayerNoTemplateEnd}
          </p>
        </div>
      )}
      <div className="border-t border-border-subtle pt-16">
        {what === 'new' && (
          <div className="flex flex-col gap-12">
            <TextField
              label={C.nameLabel}
              value={name}
              maxLength={100}
              onChange={(event) => setName(event.target.value)}
              testId="layers-add-below-name"
            />
            {note && <p className="text-s13 leading-h15 text-text-secondary">{note}</p>}
            {picksOwn && (
              // Its own charities, chosen now: from the list that binds them, else any charity, Cause
              // fund or collection. The attach then finds them on the list, and the lock keeps them.
              <div className="flex flex-col gap-8" data-testid="layers-add-below-recipients">
                <span className="text-s12 font-w600 tracking-givmo text-text-strong">
                  Recipients
                </span>
                {picks.length > 0 && (
                  <Chips
                    items={picks}
                    testId="layers-add-below-picks"
                    onRemove={(item) =>
                      setPicks((now) => now.filter((p) => recipientKey(p) !== recipientKey(item)))
                    }
                  />
                )}
                {binding ? (
                  <div className="flex max-h-[280px] flex-col overflow-y-auto rounded-lg border border-border-subtle p-12">
                    <PickerResults
                      loading={!listNames.ready}
                      error={false}
                      results={listOffer}
                      addedIds={pickedKeys}
                      adding={add.isPending}
                      onAdd={(result) => pick({ ...result })}
                      onRetry={() => undefined}
                      testIdPrefix="layers-add-below-recipient"
                    />
                  </div>
                ) : (
                  <RecipientSearch
                    addedIds={pickedKeys}
                    adding={add.isPending}
                    onAdd={(result) => pick({ ...result })}
                    testIdPrefix="layers-add-below-recipient"
                    compact
                  />
                )}
              </div>
            )}
          </div>
        )}
        {what === 'yours' && (
          <div className="flex flex-col gap-8">
            <span className="text-s12 font-w600 tracking-givmo text-text-strong">
              {C.yoursLabel}
            </span>
            <div className="flex max-h-[280px] flex-col gap-8 overflow-y-auto rounded-lg border border-border-subtle p-12">
              {candidates.pending ? (
                <FundraisersLoading />
              ) : (
                <>
                  {eligible.length > 0 ? (
                    <RadioTileGroup
                      value={picked}
                      onValueChange={setPicked}
                      aria-label={C.yoursLabel}
                      testId="layers-add-below-pick"
                    >
                      {eligible.map((candidate) => (
                        <RadioTile
                          key={candidate.campaign_id}
                          value={candidate.campaign_id}
                          label={candidate.campaign_name}
                          description={candidateLine(candidate)}
                          testId={`layers-add-below-pick-${candidate.campaign_id}`}
                        />
                      ))}
                    </RadioTileGroup>
                  ) : (
                    <p className="text-s13 text-text-secondary">{C.noYours}</p>
                  )}
                  {taken.length > 0 && (
                    <div
                      className="mt-4 flex flex-col gap-8 border-t border-border-subtle pt-12"
                      data-testid="layers-add-below-taken"
                    >
                      <span className="text-s12 font-w600 tracking-givmo text-text-secondary">
                        {C.candidatesTaken}
                      </span>
                      <ul className="flex flex-col gap-8">
                        {taken.map((candidate) => (
                          <li
                            key={candidate.campaign_id}
                            className="flex flex-col"
                            data-testid={`layers-add-below-taken-${candidate.campaign_id}`}
                          >
                            <span className="text-s14 font-w500 text-text-secondary">
                              {candidate.campaign_name}
                            </span>
                            {candidate.part_of && (
                              <span className="text-s13 text-text-secondary">
                                {C.candidatePartOf(candidate.part_of.campaign_name)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div ref={candidates.end} aria-hidden />
                </>
              )}
            </div>
          </div>
        )}
        {what === 'others' && (
          <div className="flex flex-col gap-12">
            <TextField
              label={C.linkLabel}
              value={link}
              onChange={(event) => setLink(event.target.value)}
              testId="layers-add-below-link"
            />
            <p className="-mt-4 text-s12 text-text-secondary">{C.linkHelp}</p>
            {preview.isSuccess && (
              <div
                className="flex items-center gap-12 rounded-lg border border-border-subtle px-12 py-10"
                data-testid="layers-add-below-preview"
              >
                <Avatar name={preview.data.campaign_name} size="sm" />
                <div className="min-w-0">
                  <p className="text-s14 font-w600 text-text-strong">
                    {preview.data.campaign_name}
                  </p>
                  {byline.data && (
                    <p className="text-s12 text-text-secondary">{C.organizedBy(byline.data)}</p>
                  )}
                </div>
              </div>
            )}
            {refusedHere ? (
              <StateNotice icon={CircleAlert} testId="layers-add-below-refusal" action={null}>
                {inviteRefusal.message}
              </StateNotice>
            ) : (
              <p className="text-s13 leading-h15 text-text-secondary">{C.inviteNote}</p>
            )}
          </div>
        )}
      </div>
    </DialogFrame>
  );
}

/** "Active · $1,240.00 raised": a candidate's state and full total. */
function candidateLine(candidate: P2pLayerCandidateOut): string {
  return `${fundraiserStateLabel(candidate.state)} · ${displayP2pMoney(candidate.raised_amount)} raised`;
}

export function AddAboveDialog({
  target,
  self,
  config,
  selfCharities,
  onClose,
  onDone,
}: {
  /** The top campaign the new one goes over; `null` while the dialog is closed. */
  target: LayerTarget | null;
  self: LayerTarget;
  config: P2pConfigOut | null;
  selfCharities: readonly string[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const module = useFundraisersModule();
  const client = useQueryClient();
  const live = useLivePage();
  const open = target !== null;
  const row = target ?? self;
  const isSelf = row.id === self.id;
  const [what, setWhat] = useState('new');
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  useEffect(() => {
    // Each open starts fresh.
    if (!open) {
      setWhat('new');
      setName('');
      setLink('');
    }
  }, [open]);
  // FCM-WEB33: the campaign a pasted link names, and what its ONE join setting lets this one do.
  const linkedId = what === 'others' ? campaignIdFromLink(link) : null;
  const preview = useQuery({
    ...joinParentPreviewOptions(row.id, linkedId ?? ''),
    enabled: open && linkedId !== null,
    retry: false,
  });
  const found = linkedId !== null && preview.isSuccess ? preview.data : null;
  const parentName = found?.parent_campaign_name ?? C.anotherCampaign;
  // What that campaign requires of its layers, for the terms' fifth (as the invitation names it).
  const requires = found?.requires ?? null;
  const kind = requires?.recipient_choice === 'supporters' ? 'list' : 'campaign';
  const required = useRecipientNames(
    requires === null ? null : kind === 'list' ? requires.binding_list : requires,
    found !== null,
  );
  // Joining or asking, the six ruled terms first (decision 9): only where this campaign may do either.
  const way = found?.layer_join ?? null;
  const offered =
    found !== null &&
    (way === 'join' || way === 'ask') &&
    found.join_open &&
    found.declined_at == null &&
    found.fit.can_add;
  const terms = offered
    ? C.inviteTerms(
        parentName,
        required.ready && (kind === 'campaign' || requires?.binding_list != null)
          ? { kind, charities: required.items.map((item) => item.name) }
          : null,
      )
    : null;
  const add = useMutation({
    mutationFn: async (): Promise<'added' | 'joined' | 'asked'> => {
      if (what === 'others') {
        const { data } = await sdk.campaignsJoinCampaignParent({
          path: { fundraiser_id: row.id },
          body: { parent_campaign_id: linkedId ?? '' },
          throwOnError: true,
        });
        return data.outcome === 'joined' ? 'joined' : 'asked';
      }
      if (!module.orgRef) throw new Error('This organization is not ready.');
      const below = await client.fetchQuery(campaignOptions(row.id));
      const pagesPick = below.config.recipient_choice === 'supporters';
      const charities = await charitiesFor(client, row.id, module.flavor.recipientMode);
      const { data: created } = await sdk.orgFundraisersCreateOrgFundraiser({
        path: { org_ref: module.orgRef },
        body: { name: name.trim(), show_statistics: true, ...charities },
        throwOnError: true,
      });
      // A campaign whose pages and layers pick their charities sits only under one that lets them.
      const enable: P2pConfigRequest =
        pagesPick && recipientChoiceLive(below.config)
          ? { ...TEAMS_OFF, recipient_choice: 'supporters' }
          : TEAMS_OFF;
      await sdk.campaignsEnableCampaign({
        path: { fundraiser_id: created.id },
        body: enable,
        throwOnError: true,
      });
      await sdk.campaignsAttachCampaignLayer({
        path: { fundraiser_id: created.id },
        body: { child_campaign_id: row.id },
        throwOnError: true,
      });
      return 'added';
    },
    onSuccess: async (outcome) => {
      await Promise.all([onDone(), invalidateRequestReads(client)]);
      if (live()) {
        onClose();
        toast.success(
          outcome === 'joined'
            ? R.joinedToast(parentName)
            : outcome === 'asked'
              ? R.askedToast(parentName)
              : C.layerAddedAbove,
        );
      }
    },
    onError: (error) => {
      if (!live()) return;
      const refusal = layerRefusal(error);
      toast.error(refusal.message);
      if (refusal.stale) {
        onClose();
        void onDone();
      }
    },
  });
  const note =
    module.flavor.recipientMode === 'self'
      ? C.raisesForLike(module.orgName, row.name)
      : !isSelf
        ? null
        : config?.recipient_choice === 'supporters'
          ? C.letsPagesPickLike(row.name)
          : selfCharities.length > 0
            ? C.raisesForLike(series(selfCharities), row.name)
            : null;
  // Consent fails closed: Join or Request waits until every term it consents to is shown.
  const ready =
    what === 'new' ? name.trim().length > 0 : offered && required.ready && terms !== null;
  const sticky = what === 'others' && terms !== null;
  return (
    <DialogFrame
      open={open}
      onOpenChange={(next) => {
        if (!next && !add.isPending) onClose();
      }}
      title={C.addAbove}
      testId="layers-add-above-dialog"
      width={sticky ? 560 : undefined}
      sticky={sticky}
      footerNote={
        sticky ? (
          <p
            className="text-s14 font-w600 leading-h15 text-text-strong"
            data-testid="layers-add-above-consent"
          >
            {way === 'join' ? R.consentJoin : R.consentAsk}
          </p>
        ) : null
      }
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={add.isPending}
            onClick={onClose}
            testId="layers-add-above-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            loading={add.isPending}
            disabled={!ready || add.isPending}
            onClick={() => add.mutate()}
            testId="layers-add-above-confirm"
          >
            {what === 'new' ? C.addAboveConfirm : way === 'join' ? R.joinConfirm : R.askConfirm}
          </Button>
        </>
      }
    >
      <RadioTileGroup
        value={what}
        onValueChange={setWhat}
        aria-label={C.whatToAdd}
        testId="layers-add-above-what"
      >
        <RadioTile
          value="new"
          label={C.optNew}
          description={C.optNewDesc}
          testId="layers-add-above-new"
        />
        <RadioTile
          value="others"
          label={C.optOthers}
          description={R.aboveOthersDescription}
          testId="layers-add-above-others"
        />
      </RadioTileGroup>
      <div className="flex flex-col gap-12 border-t border-border-subtle pt-16">
        {what === 'new' && (
          <>
            <p className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary">
              {C.addAboveBody(row.name)}
            </p>
            <TextField
              label={C.nameLabel}
              value={name}
              maxLength={100}
              onChange={(event) => setName(event.target.value)}
              testId="layers-add-above-name"
            />
            {note && <p className="-mt-4 text-s13 leading-h15 text-text-secondary">{note}</p>}
          </>
        )}
        {what === 'others' && (
          <>
            <TextField
              label={C.linkLabel}
              value={link}
              onChange={(event) => setLink(event.target.value)}
              testId="layers-add-above-link"
            />
            <p className="-mt-4 text-s12 text-text-secondary">{C.linkHelp}</p>
            {found !== null && (
              <div
                className="flex items-center gap-12 rounded-lg border border-border-subtle px-12 py-10"
                data-testid="layers-add-above-preview"
              >
                <Avatar name={found.parent_organization_name ?? parentName} size="sm" />
                <div className="min-w-0">
                  <p className="text-s14 font-w600 text-text-strong">{parentName}</p>
                  {found.parent_organization_name && (
                    <p className="text-s12 text-text-secondary">
                      {C.organizedBy(found.parent_organization_name)}
                    </p>
                  )}
                </div>
              </div>
            )}
            {linkedId !== null && preview.isError && (
              // The campaign can't be asked or joined from here: the server's own words.
              <StateNotice icon={CircleAlert} testId="layers-add-above-refusal" action={null}>
                {layerRefusal(preview.error).message}
              </StateNotice>
            )}
            {found !== null && (
              <JoinOutcome preview={found} parentName={parentName} listKind={kind} terms={terms} />
            )}
          </>
        )}
      </div>
    </DialogFrame>
  );
}

/**
 * What the pasted campaign's setting lets this one do (frames 05 to 05e), in this order: a decline,
 * which is final for the pair; paused joins; a setting that admits no campaign without its invitation;
 * a campaign this one can't fit; and otherwise the way it joins, then the six terms.
 */
function JoinOutcome({
  preview,
  parentName,
  listKind,
  terms,
}: {
  preview: P2pLayerJoinPreviewOut;
  parentName: string;
  listKind: 'campaign' | 'list';
  terms: string[] | null;
}) {
  const way = preview.layer_join;
  const notice = (testId: string, text: string) => (
    <StateNotice icon={CircleAlert} testId={testId} action={null}>
      {text}
    </StateNotice>
  );
  if (preview.declined_at != null)
    return notice('layers-add-above-declined', REQUEST_SERVER_WORDS.declined(parentName));
  if (way === 'invitation')
    return notice('layers-add-above-invitation', R.outcomeInvite(parentName));
  if (way === 'members')
    return notice(
      'layers-add-above-members',
      preview.parent_organization_name
        ? R.outcomeMembers(parentName, preview.parent_organization_name)
        : R.outcomeInvite(parentName),
    );
  if (!preview.join_open)
    return notice('layers-add-above-paused', REQUEST_SERVER_WORDS.paused(parentName));
  if (!preview.fit.can_add)
    return notice(
      'layers-add-above-no-fit',
      preview.fit.refusal_code === 'CAMPAIGN_LAYER_RECIPIENTS'
        ? R.outcomeNoFit(parentName, listKind)
        : (preview.fit.refusal ?? R.outcomeNoFit(parentName, listKind)),
    );
  if (terms === null) return null;
  return (
    <div className="flex flex-col gap-12" data-testid={`layers-add-above-${way}`}>
      {way === 'ask' && preview.removed_at ? (
        <StateNotice icon={Info} testId="layers-add-above-removed" action={null}>
          {R.outcomeRemoved(parentName, displayP2pDate(preview.removed_at))}
        </StateNotice>
      ) : (
        <p className="text-s14 leading-h15 text-text-strong" data-testid="layers-add-above-outcome">
          {way === 'join' ? R.outcomeJoin(parentName) : R.outcomeAsk(parentName)}
        </p>
      )}
      <p className="text-s14 font-w600 text-text-strong">
        {way === 'join' ? R.ifJoins : R.ifApproved}
      </p>
      <ul
        className="flex list-disc flex-col gap-8 pl-20 text-s13 leading-h15 text-text-secondary"
        data-testid="layers-add-above-terms"
      >
        {terms.map((term) => (
          <li key={term}>{term}</li>
        ))}
      </ul>
    </div>
  );
}

export function MoveDialog({
  move,
  onClose,
  onDone,
}: {
  /** The layer, and the campaign it moves out of (its parent, on whose side Move sits). */
  move: { layer: P2pLayerNodeOut; from: LayerTarget } | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const live = useLivePage();
  const client = useQueryClient();
  const [target, setTarget] = useState('');
  const open = move !== null;
  const from = move?.from ?? { id: '', name: '' };
  const candidates = useCandidatePages(from.id, open, move?.layer.campaign_id);
  useEffect(() => {
    if (!open) setTarget('');
  }, [open]);
  const moving = useMutation({
    mutationFn: () =>
      sdk.campaignsMoveCampaignLayer({
        path: { fundraiser_id: move?.layer.campaign_id ?? '' },
        body: { parent_campaign_id: target, from_campaign_id: from.id },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await onDone();
      if (live()) {
        onClose();
        toast.success(C.layerMoved);
      }
    },
    onError: async (error) => {
      if (!live()) return;
      const refusal = layerRefusal(error);
      toast.error(refusal.message);
      setTarget('');
      candidates.reset();
      await invalidateLayerReads(client);
      if (refusal.stale) {
        onClose();
        void onDone();
      }
    },
  });
  const layerName = move?.layer.campaign_name ?? C.anotherCampaign;
  return (
    <DialogFrame
      open={open}
      onOpenChange={(next) => {
        if (!next && !moving.isPending) onClose();
      }}
      title={C.moveTitle(layerName)}
      description={C.moveBody(from.name)}
      testId="layers-move-dialog"
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={moving.isPending}
            onClick={onClose}
            testId="layers-move-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            loading={moving.isPending}
            disabled={target === '' || moving.isPending}
            onClick={() => moving.mutate()}
            testId="layers-move-confirm"
          >
            {C.moveConfirm}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-8">
        <span className="text-s12 font-w600 tracking-givmo text-text-strong">{C.moveUnder}</span>
        <div className="flex max-h-[300px] flex-col gap-8 overflow-y-auto rounded-lg border border-border-subtle p-12">
          {candidates.pending ? (
            <FundraisersLoading />
          ) : candidates.rows.length > 0 ? (
            <RadioTileGroup
              value={target}
              onValueChange={setTarget}
              aria-label={C.moveUnder}
              testId="layers-move-pick"
            >
              {candidates.rows.map((candidate) => (
                <RadioTile
                  key={candidate.campaign_id}
                  value={candidate.campaign_id}
                  label={candidate.campaign_name}
                  description={
                    candidate.part_of
                      ? `${fundraiserStateLabel(candidate.state)} · ${C.candidatePartOf(candidate.part_of.campaign_name)}`
                      : fundraiserStateLabel(candidate.state)
                  }
                  testId={`layers-move-pick-${candidate.campaign_id}`}
                />
              ))}
            </RadioTileGroup>
          ) : (
            <p className="text-s13 text-text-secondary">{C.noYours}</p>
          )}
          <div ref={candidates.end} aria-hidden />
        </div>
      </div>
    </DialogFrame>
  );
}

/**
 * Decision 3 (RULED 2026-10-06 23:05 PDT): which level's card the pages under this campaign show, any level
 * from itself up to the top; inherited downward, a lower choice winning. Its words are the ruled card line's
 * and the server's own `CARD_LEVEL_DETAIL` (D-FCM-46).
 */
export function CardLevelDialog({
  open,
  campaignId,
  tree,
  chosen,
  onClose,
  onDone,
}: {
  open: boolean;
  campaignId: string;
  tree: P2pLayerStructureOut;
  /** This campaign's own stored choice, if it made one. */
  chosen: string | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const live = useLivePage();
  const shown = chosen ?? tree.card?.campaign_id ?? campaignId;
  const [level, setLevel] = useState(shown);
  useEffect(() => {
    if (open) setLevel(shown);
  }, [open, shown]);
  // A campaign above whose name the person may not see is no choice (NF-BE26).
  const levels = tree.path.filter((node) => node.campaign_name != null);
  const save = useMutation({
    mutationFn: () =>
      sdk.campaignsUpdateCampaign({
        path: { fundraiser_id: campaignId },
        body: { card_campaign_id: level },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await onDone();
      if (live()) {
        onClose();
        toast.success('Campaign settings updated');
      }
    },
    onError: (error) => {
      if (!live()) return;
      const refusal = layerRefusal(error);
      toast.error(refusal.message);
      if (refusal.stale) {
        onClose();
        void onDone();
      }
    },
  });
  return (
    <DialogFrame
      open={open}
      onOpenChange={(next) => {
        if (!next && !save.isPending) onClose();
      }}
      title={C.cardTitle}
      description={SERVER_WORDS.cardLevel}
      testId="layers-card-level-dialog"
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="layers-card-level-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            loading={save.isPending}
            disabled={level === shown || save.isPending}
            onClick={() => save.mutate()}
            testId="layers-card-level-save"
          >
            {C.styleSave}
          </Button>
        </>
      }
    >
      <RadioTileGroup
        value={level}
        onValueChange={setLevel}
        aria-label={C.cardTitle}
        testId="layers-card-level-pick"
      >
        {levels.map((node) => (
          <RadioTile
            key={node.campaign_id}
            value={node.campaign_id}
            label={node.campaign_name ?? ''}
            testId={`layers-card-level-${node.campaign_id}`}
          />
        ))}
      </RadioTileGroup>
    </DialogFrame>
  );
}
